import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import {
  createDarkenedColor,
  createHoverBackground,
} from '../utils/ThemeUtils';

interface RemoveTSPModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmRemove: () => void;
  tspName: string;
  isLoading?: boolean;
}

const RemoveTSPModal: React.FC<RemoveTSPModalProps> = ({
  open,
  onClose,
  onConfirmRemove,
  tspName,
  isLoading = false,
}) => {
  const theme = useTheme();
  const primaryColor = (theme.palette?.primary?.main as string) || '#B60207';

  // Use utility functions for hover colors
  const hoverBackground = createHoverBackground(primaryColor, 0.04);
  const darkenedPrimaryColor = createDarkenedColor(primaryColor);

  const handleConfirmRemove = () => {
    amplitude.track('User Confirmed TSP Removal', {
      tspName,
      timestamp: new Date().toISOString(),
    });
    onConfirmRemove();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 32px)', sm: '420px' },
          maxWidth: { xs: 'calc(100vw - 32px)', sm: '420px' },
          margin: { xs: 2, sm: 'auto' },
          borderRadius: '4px',
          boxShadow:
            '0px 4px 8px rgba(0, 0, 0, 0.12), 0px 6px 12px rgba(0, 0, 0, 0.12), 0px 1px 16px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid #DCDEDF',
          padding: { xs: '16px 16px', sm: '18px 24px' },
          fontSize: { xs: '18px', sm: '21.2px' },
          fontWeight: 600,
          letterSpacing: '-0.15px',
          position: 'relative',
          pr: { xs: '48px', sm: '56px' }, // Extra space for close button
        }}
      >
        Remove TSP Connection
        <IconButton
          aria-label='close'
          onClick={onClose}
          disabled={isLoading}
          sx={{
            position: 'absolute',
            right: { xs: 8, sm: 8 },
            top: { xs: 8, sm: 8 },
            width: { xs: '40px', sm: '40px' },
            height: { xs: '40px', sm: '40px' },
            color: '#5C656A',
            '&:hover': {
              backgroundColor: hoverBackground,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: '16px 16px 24px', sm: '16px 24px 24px' },
          minHeight: { xs: 'auto', sm: '160px' },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            lineHeight: { xs: '20px', sm: '24px' },
            letterSpacing: '0.15px',
            color: '#263238',
            marginTop: { xs: '12px', sm: '16px' },
            marginBottom: { xs: '16px', sm: '24px' },
          }}
        >
          Are you sure you want to remove {tspName}?
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: { xs: '20px', sm: '24px' },
            letterSpacing: '0.15px',
            color: '#263238',
          }}
        >
          This will permanently disconnect {tspName} from your fleet and stop
          all data syncing from this provider.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          padding: { xs: '16px 16px 24px', sm: '16px 24px 24px' },
          gap: { xs: '8px', sm: '8px' },
          justifyContent: 'flex-end',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          onClick={handleConfirmRemove}
          disabled={isLoading}
          sx={{
            fontSize: { xs: '16px', sm: '14px' },
            fontWeight: 700,
            letterSpacing: '0.75px',
            textTransform: 'uppercase',
            padding: { xs: '12px 16px', sm: '12px' },
            height: { xs: '48px', sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 2, sm: 1 },
            color: primaryColor,
            '&:hover': {
              backgroundColor: hoverBackground,
            },
            '&:disabled': {
              color: '#C7CACB',
            },
          }}
        >
          {isLoading ? 'REMOVING...' : 'YES, REMOVE THIS CONNECTION'}
        </Button>
        <Button
          variant='contained'
          onClick={onClose}
          disabled={isLoading}
          sx={{
            backgroundColor: primaryColor,
            color: '#FFFFFF',
            fontSize: { xs: '16px', sm: '14px' },
            fontWeight: 700,
            letterSpacing: '0.75px',
            textTransform: 'uppercase',
            padding: { xs: '12px 16px', sm: '12px' },
            height: { xs: '48px', sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 1, sm: 2 },
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: darkenedPrimaryColor,
            },
            '&:disabled': {
              backgroundColor: '#C7CACB',
              color: '#FFFFFF',
            },
          }}
        >
          KEEP THIS CONNECTION
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveTSPModal;
